import React, { useEffect, useState } from "react";
import { FiBell, FiExternalLink, FiTrash2, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { getNotificationById } from "../../api/notifications";
import { getUserFacingError, isAuthenticationError } from "../../utils/userFacingError";
import { getNotificationActionLabel, getNotificationActionUrl, getNotificationDestinationDescription } from "../../utils/notificationNavigation";

const formatDateTime = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

export default function NotificationDetailsModal({ notification, onClose, onDelete }) {
  const navigate = useNavigate();
  const [details, setDetails] = useState(notification || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    setDetails(notification || null);
    setError("");
    setDeleting(false);
    setConfirmingDelete(false);
    if (!notification?.id) return undefined;

    let active = true;
    setLoading(true);
    getNotificationById(notification.id)
      .then((response) => {
        if (active) setDetails(response?.notification || notification);
      })
      .catch((requestError) => {
        if (active) setError(isAuthenticationError(requestError) ? "" : getUserFacingError(requestError, "Some additional details could not be loaded."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [notification]);

  useEffect(() => {
    if (!notification) return undefined;
    const handleKeyDown = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [notification, onClose]);

  if (!notification) return null;

  const date = details?.deliveredAt || details?.sentAt || details?.createdAt;
  const actionUrl = getNotificationActionUrl(details);
  const destination = getNotificationDestinationDescription(details);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="notification-modal-title" className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="sticky top-0 flex items-start justify-between gap-4 border-b bg-white p-5 sm:p-6">
          <div className="flex min-w-0 items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1234CD]"><FiBell /></span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#1234CD]">{details?.type || "Notification"}</p>
              <h2 id="notification-modal-title" className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">{details?.title || "Notification details"}</h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Close notification details"><FiX /></button>
        </div>
        <div className="space-y-5 p-5 sm:p-6">
          <div className="rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-700">{details?.message || "No additional message was provided."}</div>
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div><dt className="text-slate-400">Date and time</dt><dd className="mt-1 font-medium text-slate-700">{formatDateTime(date)}</dd></div>
            <div><dt className="text-slate-400">Status</dt><dd className="mt-1 font-medium capitalize text-slate-700">{details?.status || (details?.isRead ? "Read" : "Unread")}</dd></div>
            {details?.senderEmail ? <div><dt className="text-slate-400">Sender</dt><dd className="mt-1 break-all font-medium text-slate-700">{details.senderEmail}</dd></div> : null}
            {details?.channel ? <div><dt className="text-slate-400">Channel</dt><dd className="mt-1 font-medium capitalize text-slate-700">{details.channel}</dd></div> : null}
          </dl>
          {loading ? <p className="text-xs text-slate-400">Loading complete notification details…</p> : null}
          {error ? <p className="text-xs text-amber-600">{error} Showing the available notification details.</p> : null}
          <div className="border-t border-slate-100 pt-4">
            {actionUrl ? (
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {destination?.isDirect ? "Direct destination" : "Related workspace"}
                  </p>
                  <p className="mt-1 text-sm text-slate-700">
                    {destination?.isDirect
                      ? `Opens ${destination.location}`
                      : `No item-specific link was saved. Open ${destination?.location} to find the related item.`}
                  </p>
                </div>
                <button type="button" onClick={() => { onClose(); navigate(actionUrl); }} className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#1234CD] px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"><FiExternalLink />{getNotificationActionLabel(details)}</button>
              </div>
            ) : (
              <div className="rounded-xl bg-slate-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">No linked destination</p>
                <p className="mt-1 text-sm text-slate-600">This notification does not include a route to another page.</p>
              </div>
            )}
          </div>
          {onDelete ? <div className="border-t border-slate-100 pt-4">
            {confirmingDelete ? <div className="rounded-xl border border-red-100 bg-red-50 p-4"><p className="text-sm font-semibold text-red-800">Delete this notification?</p><p className="mt-1 text-xs text-red-600">It will be removed from your notification list.</p><div className="mt-3 flex justify-end gap-2"><button type="button" onClick={() => setConfirmingDelete(false)} disabled={deleting} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700">Cancel</button><button type="button" disabled={deleting} onClick={async () => { try { setDeleting(true); setError(""); await onDelete(details || notification); onClose(); } catch (deleteError) { setError(getUserFacingError(deleteError, "Unable to delete notification.")); setDeleting(false); } }} className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">{deleting ? "Deleting..." : "Delete"}</button></div></div> : <button type="button" onClick={() => setConfirmingDelete(true)} className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"><FiTrash2 />Delete notification</button>}
          </div> : null}
        </div>
      </div>
    </div>
  );
}
